/**
 * UpcomingMilestones
 *
 * Calendar-based widget that displays milestones for the current and next month.
 * Highlights dates with milestones, supports selection to filter the visible list,
 * and provides navigation between months when additional milestones exist.
 *
 * Expects parent-managed state for currentMonth and selection handlers to
 * open initiative/objective drawers via callbacks.
 */
import React, { useEffect, useMemo, useRef } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar, PickersDay } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import { Box, IconButton } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import Status from "../../request-dashboard/request/status/status";
import "./upcoming-milestones.scss";

const UpcomingMilestones: any = (props: any) => {
  dayjs.extend(isSameOrBefore);
  dayjs.extend(isSameOrAfter);
  const {
    milestonesList,
    currentMonth,
    setCurrentMonth,
    setInitiativeId,
    setObjectiveId,
    setKeyResultId,
    setObjective,
    objective,
    setIsShowAddInitiativeTableDrawer,
    setIsFromObjective,
  } = props;

  const [selectedDatesOne, setSelectedDatesOne] = React.useState<any[]>([]);
  const [selectedDatesTwo, setSelectedDatesTwo] = React.useState<any[]>([]);
  const [oneDate, setOneDate] = React.useState<any>(null);
  const [twoDate, setTwoDate] = React.useState<any>(null);
  const [selectedMilestones, setSelectedMilestones] = React.useState<any[]>([]);
  const milestonesEndRef: any = useRef(null);

  // Add a refresh counter to force re-evaluation when milestones change
  const [refreshKey, setRefreshKey] = React.useState(0);

  // Force refresh when milestonesList changes
  useEffect(() => {
    setRefreshKey((prev) => prev + 1);
  }, [milestonesList]);

  // Filter milestones for the currently visible two months
  const visibleMilestones = useMemo(() => {
    if (!milestonesList || milestonesList.length === 0) return [];

    const firstMonthStart = currentMonth.startOf("month");
    const secondMonthEnd = currentMonth.add(1, "month").endOf("month");

    return milestonesList.filter((milestone: any) => {
      const milestoneDate = dayjs(milestone?.endDate);
      return (
        milestoneDate.isSameOrAfter(firstMonthStart, "day") &&
        milestoneDate.isSameOrBefore(secondMonthEnd, "day")
      );
    });
  }, [milestonesList, currentMonth, refreshKey]);

  // Check if there are milestones after the currently visible months
  const hasNextMonthMilestones = useMemo(() => {
    if (!milestonesList || milestonesList.length === 0) return false;

    const secondMonthEnd = currentMonth.add(1, "month").endOf("month");
    return milestonesList.some((milestone: any) => {
      const milestoneDate = dayjs(milestone?.endDate);
      return milestoneDate.isAfter(secondMonthEnd, "day");
    });
  }, [milestonesList, currentMonth, refreshKey]);

  // Check if there are milestones before the currently visible months
  const hasPrevMonthMilestones = useMemo(() => {
    if (!milestonesList || milestonesList.length === 0) return false;

    const firstMonthStart = currentMonth.startOf("month");
    return milestonesList.some((milestone: any) => {
      const milestoneDate = dayjs(milestone?.endDate);
      return milestoneDate.isBefore(firstMonthStart, "day");
    });
  }, [milestonesList, currentMonth, refreshKey]);

  const handlePrevMonth = () => {
    setCurrentMonth((prev: any) => prev.subtract(1, "month"));
  };

  function getDatesBetween() {
    setSelectedDatesOne([]);
    const allDates = visibleMilestones?.map((milestone: any) =>
      dayjs(milestone?.endDate),
    );
    const sorted = allDates?.sort(
      (a: any, b: any) => dayjs(a).valueOf() - dayjs(b).valueOf(),
    );
    const setOfDates = sorted.reduce((acc: any, date: any) => {
      const monthKey = dayjs(date).format("MM");
      if (!acc[monthKey]) {
        acc[monthKey] = [];
      }
      acc[monthKey].push({ date: date, isSelected: false });
      return acc;
    }, {});
    setSelectedDatesOne(setOfDates[dayjs(currentMonth).format("MM")] || []);
    setSelectedDatesTwo(
      setOfDates[dayjs(currentMonth).add(1, "month").format("MM")] || [],
    );
    setSelectedMilestones(visibleMilestones || []);
  }

  useEffect(() => {
    if (visibleMilestones?.length) {
      getDatesBetween();
    } else {
      // Clear selections when no visible milestones
      setSelectedDatesOne([]);
      setSelectedDatesTwo([]);
      setSelectedMilestones([]);
    }
  }, [visibleMilestones, currentMonth]);

  useEffect(() => {
    const findMilestonesOne = selectedDatesOne?.find(
      (dates: any) => dates?.isSelected,
    );
    const selectedMilestonesOne = visibleMilestones?.filter(
      (milestone: any) =>
        dayjs(findMilestonesOne?.date).isSame(milestone?.endDate, "day") &&
        dayjs(findMilestonesOne?.date).isSame(milestone?.endDate, "month"),
    );
    if (findMilestonesOne) {
      setSelectedMilestones([...selectedMilestonesOne]);
    }
  }, [selectedDatesOne, visibleMilestones]);

  useEffect(() => {
    const findMilestonesTwo = selectedDatesTwo?.find(
      (dates: any) => dates?.isSelected,
    );
    const selectedMilestonesTwo = visibleMilestones?.filter(
      (milestone: any) =>
        dayjs(findMilestonesTwo?.date).isSame(milestone?.endDate, "day") &&
        dayjs(findMilestonesTwo?.date).isSame(milestone?.endDate, "month"),
    );
    if (findMilestonesTwo) {
      setSelectedMilestones([...selectedMilestonesTwo]);
    }
  }, [selectedDatesTwo, visibleMilestones]);

  useEffect(() => {
    if (!twoDate && !oneDate) {
      setSelectedMilestones(visibleMilestones);
    }
  }, [visibleMilestones]);

  const handleDateOneChange = (date?: any, newdate?: any) => {
    if (!date && !newdate) {
      setTwoDate(null);
      setOneDate(null);
      setSelectedDatesOne(
        selectedDatesOne.map((d) => ({ ...d, isSelected: false })),
      );
      setSelectedDatesTwo(
        selectedDatesTwo.map((d) => ({ ...d, isSelected: false })),
      );
      setSelectedMilestones(visibleMilestones);
    } else {
      const isDataFromSetOne = selectedDatesOne.find(
        (d) =>
          dayjs(d?.date).isSame(date, "day") &&
          dayjs(d?.date).isSame(date, "month"),
      );

      if (isDataFromSetOne) {
        setSelectedDatesOne(
          selectedDatesOne.map((d) => {
            if (
              dayjs(d?.date).isSame(date, "day") &&
              dayjs(d?.date).isSame(date, "month")
            ) {
              return { ...d, isSelected: true };
            }
            return { ...d, isSelected: false };
          }),
        );
        setSelectedDatesTwo(
          selectedDatesTwo.map((d) => ({ ...d, isSelected: false })),
        );
      }
      setTimeout(() => {
        milestonesEndRef.current?.firstElementChild?.scrollIntoView({
          behavior: "smooth",
        });
      }, 100);
    }
  };

  const handleDateTwoChange = (date: any) => {
    if (date) {
      const isDataFromSetTwo = selectedDatesTwo.find(
        (d) =>
          dayjs(d?.date).isSame(date, "day") &&
          dayjs(d?.date).isSame(date, "month"),
      );
      if (isDataFromSetTwo) {
        setSelectedDatesTwo(
          selectedDatesTwo.map((d) => {
            if (
              dayjs(d?.date).isSame(date, "day") &&
              dayjs(d?.date).isSame(date, "month")
            ) {
              return { ...d, isSelected: true };
            }
            return { ...d, isSelected: false };
          }),
        );
        setSelectedDatesOne(
          selectedDatesOne.map((d) => ({ ...d, isSelected: false })),
        );
      }
      setTimeout(() => {
        milestonesEndRef.current?.firstElementChild?.scrollIntoView({
          behavior: "smooth",
        });
      }, 100);
    } else {
      setTwoDate(null);
      setOneDate(null);
      setSelectedDatesOne(
        selectedDatesOne.map((d) => ({ ...d, isSelected: false })),
      );
      setSelectedDatesTwo(
        selectedDatesTwo.map((d) => ({ ...d, isSelected: false })),
      );
      setSelectedMilestones(visibleMilestones);
    }
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev: any) => prev.add(1, "month"));
  };

  const CustomDay = styled(PickersDay)(({ theme, selected }) => {
    return {
      ...(selected && {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        "&:hover, &:focus": {
          backgroundColor: theme.palette.primary.dark,
        },
      }),
    };
  });

   const slotsDateCalendar = (props: any) => {
                    const isSelected = selectedDatesOne.some((d) =>
                      dayjs(d?.date).isSame(props.day, "day"),
                    );
                    const isSelectedDate = selectedDatesOne.find((d) =>
                      dayjs(d?.date).isSame(props.day, "day"),
                    );

                    return isSelectedDate?.isSelected ? (
                      <CustomDay
                        {...props}
                        className={
                          isSelectedDate?.isSelected
                            ? "custom-selected-show-day"
                            : ""
                        }
                        selected={isSelected}
                      />
                    ) : (
                      <CustomDay
                        {...props}
                        className={isSelected ? "custom-selected-day" : ""}
                        selected={isSelected}
                      />
                    );
                  }
    const slotsDateCalendarTwo = (props: any) => {
                    const isSelected = selectedDatesTwo.some((d) =>
                      dayjs(d?.date).isSame(props.day, "day"),
                    );
                    const isSelectedDate = selectedDatesTwo.find((d) =>
                      dayjs(d?.date).isSame(props.day, "day"),
                    );

                    return isSelectedDate?.isSelected ? (
                      <CustomDay
                        {...props}
                        className={
                          isSelectedDate?.isSelected
                            ? "custom-selected-show-day"
                            : ""
                        }
                        selected={isSelectedDate}
                      />
                    ) : (
                      <CustomDay
                        {...props}
                        className={isSelected ? "custom-selected-day" : ""}
                        selected={isSelected}
                      />
                    );
                  }

   return (
    <div className="upcoming-milestones">
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div className="upcoming-milestones-content-header">
          Upcoming Milestones
          <Box>
            <IconButton
              onClick={handlePrevMonth}
              disabled={!hasPrevMonthMilestones}
              sx={{
                cursor: hasPrevMonthMilestones
                  ? "pointer"
                  : "not-allowed !important",
                "&.Mui-disabled": {
                  opacity: 0.5,
                  cursor: "not-allowed !important",
                },
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
            <IconButton
              onClick={handleNextMonth}
              disabled={!hasNextMonthMilestones}
              sx={{
                cursor: hasNextMonthMilestones
                  ? "pointer"
                  : "not-allowed !important",
                "&.Mui-disabled": {
                  opacity: 0.5,
                  cursor: "not-allowed !important",
                },
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
        </div>
        <div className="upcoming-milestones-content">
          <OverlayScrollbarsComponent
            className="upcoming-milestones-content-section"
            options={{
              scrollbars: {
                theme: "os-theme-dark",
                autoHide: "scroll",
                autoHideDelay: 400,
              },
            }}
          >
            <button
              className="upcoming-milestones-content-section-calendar-one"
              onClick={() => handleDateOneChange(null, null)}
            >
              <DateCalendar
                value={currentMonth}
                onChange={(date?: any, newdate?: any) =>
                  setTimeout(() => handleDateOneChange(date, newdate), 100)
                }
                sx={{
                  "& .MuiPickersArrowSwitcher-root": {
                    display: "none",
                  },
                }}
                slots={{
                  day: slotsDateCalendar,
                }}
              />
            </button>
            <button
              className="upcoming-milestones-content-section-calendar-two"
              onClick={() => handleDateTwoChange(null)}
            >
              <DateCalendar
                value={currentMonth.add(1, "month")}
                onChange={(date?: any) =>
                  setTimeout(() => handleDateTwoChange(date), 100)
                }
                sx={{
                  "& .MuiPickersArrowSwitcher-root": {
                    display: "none",
                  },
                }}
                slots={{
                  day: slotsDateCalendarTwo,
                }}
              />
            </button>

            <div
              className="upcoming-milestones-content-section-info-cards"
              ref={milestonesEndRef}
            >
              {selectedMilestones.map((milestone: any) => (
                <button
                  className="upcoming-milestones-content-section-info-cards-item"
                  key={milestone?.milestoneId}
                  onClick={() => {
                    if (objective === "Objectives") {
                      setIsFromObjective("upcoming milestones");
                    }

                    setObjective("Initiatives");
                    setIsShowAddInitiativeTableDrawer(true);
                    setInitiativeId(milestone?.initiative?.initiativeId);
                    setObjectiveId(milestone?.objective?.pk);
                    setKeyResultId(milestone?.keyResult?.keyResultId);
                  }}
                >
                  <div className="upcoming-milestones-content-section-info-cards-item-date">
                    <div className="upcoming-milestones-content-section-info-cards-item-date-text">
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path
                          d="M4.89642 2.92838C3.75416 2.92838 2.82486 3.85769 2.82486 4.99994C2.82486 6.1422 3.75417 7.0715 4.89642 7.0715C6.03868 7.0715 6.96798 6.14219 6.96798 4.99994C6.96798 3.85768 6.03867 2.92838 4.89642 2.92838ZM4.89642 0.103516C7.60064 0.103516 9.79284 2.29573 9.79284 4.99994C9.79284 7.70416 7.60063 9.89636 4.89642 9.89636C2.19221 9.89636 0 7.70415 0 4.99994C0 2.29573 2.19221 0.103516 4.89642 0.103516Z"
                          fill="#403C6F"
                        />
                      </svg>{" "}
                      {dayjs(milestone?.endDate).format("MM-DD-YYYY")}
                    </div>
                    <div className="upcoming-milestones-content-section-info-cards-item-date-status">
                      <Status
                        status={milestone?.status}
                      />
                    </div>
                  </div>
                  <div className="upcoming-milestones-content-section-info-cards-item-text">
                    {milestone?.milestoneTitle || ""}
                  </div>
                </button>
              ))}
            </div>
          </OverlayScrollbarsComponent>
        </div>
      </LocalizationProvider>
    </div>
  );
};

export default UpcomingMilestones;
